import classnames from 'classnames';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { analyticsListeners, analyticsUtils } from '~/analytics';
import {
  appointmentDraftActions,
  appointmentDraftSelectors,
  journeyStateSelectors,
  shopsActions,
  shopsSelectors,
  tokenApi,
} from '~/api';
import {
  apiCallFinished,
  calendarSelectors,
  envConfigSelectors,
  errorMessagesActions,
  errorTypes,
  infoTypes,
  messagesActions,
  ScreenNav,
  scrollToMessageTypeAnchor,
  TitleWithSubtitle,
  withProgressBar,
  withRequiredPrecondition,
  withRestorableDraft,
} from '~/components';
import { Calendar, TimeSlots } from '~/containers';
import { isEmpty, isNotEmpty, scrollToAnchor } from '~/utils';
import { loadPartners } from '../partners/partners.actions';
import styles from './SelectDateTime.scss';

const ScrollToMessageTypeAnchor = scrollToMessageTypeAnchor(({ children }) => children);

const { SLOT_DATE_NOT_SELECTED_ERROR, SLOT_TIME_NOT_SELECTED_ERROR, SLOT_TIME_UNAVAILABLE_ERROR } =
  errorTypes;

const { triggerAvailabilityInteractionAnalytics, triggerPartnerGroupChangeAnalytics } =
  analyticsListeners;

const { getPartnerGroupName } = analyticsUtils;

export class SelectDateTime extends PureComponent {
  state = {
    isAjaxCompleted: false,
    subtitleLinkUrl: undefined,
  };

  componentDidMount() {
    const {
      isRemoteConfigEnabled,
      shopList,
      dispatchLoadShops,
      searchQuery,
      subtitle: {
        component: subtitleComponent,
        inStoreAppointmentTypeId,
        virtualAppointmentTypeId,
        virtualStoreId,
      },
    } = this.props;

    if (isRemoteConfigEnabled && !shopList.length) {
      dispatchLoadShops(searchQuery);
    }
    this.refineAppointmentAvailability();
    if (subtitleComponent) {
      this.buildSubtitleLinkUrl(inStoreAppointmentTypeId, virtualAppointmentTypeId, virtualStoreId);
    }
    window.scrollTo(0, 0);
  }

  componentDidUpdate({ mainAppointmentDraft: prevMainAppointmentDraft }) {
    const {
      slotErrorMessages,
      mainAppointmentDraft,
      relatedAppointmentTimeSlot,
      isMultipleAppointment,

      dispatchRemoveSingleErrorMessage,
    } = this.props;

    if (slotErrorMessages[SLOT_DATE_NOT_SELECTED_ERROR] && mainAppointmentDraft.dateSlot) {
      dispatchRemoveSingleErrorMessage(SLOT_DATE_NOT_SELECTED_ERROR);
    }

    if (
      slotErrorMessages[SLOT_TIME_NOT_SELECTED_ERROR] &&
      mainAppointmentDraft.dateSlot !== prevMainAppointmentDraft.dateSlot
    ) {
      dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
    }

    if (slotErrorMessages[SLOT_TIME_NOT_SELECTED_ERROR] && mainAppointmentDraft.timeSlot.from) {
      if (!isMultipleAppointment && mainAppointmentDraft.timeSlot.from) {
        dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
      } else if (mainAppointmentDraft.timeSlot.from && relatedAppointmentTimeSlot.from) {
        dispatchRemoveSingleErrorMessage(SLOT_TIME_NOT_SELECTED_ERROR);
      }
    }

    if (prevMainAppointmentDraft.dateSlot === '' && mainAppointmentDraft.dateSlot) {
      scrollToAnchor('time-slot-title');
    }
  }

  buildSubtitleLinkUrl = (inStoreAppointmentTypeId, virtualAppointmentTypeId, virtualStoreId) => {
    if (inStoreAppointmentTypeId) {
      this.setState({
        subtitleLinkUrl: `shops?appointmentTypeId=${inStoreAppointmentTypeId}`,
      });
    } else if (virtualAppointmentTypeId && virtualStoreId) {
      tokenApi
        .getInitialToken({
          appointmentTypeId: virtualAppointmentTypeId,
          storeId: virtualStoreId,
          searchQuery: 'England',
        })
        .then((token) =>
          this.setState({
            subtitleLinkUrl: `select-date?token=${token}`,
          }),
        );
    }
  };

  refineAppointmentAvailability = () => {
    const {
      mainAppointmentDraft: {
        appointmentType: { hasGroups, groups, beauty: { priceType } = {} },
      },
      dispatchResetErrorMessagesWith,
      dispatchApiCallFinished,
      dispatchClearErrorMessages,
    } = this.props;

    dispatchClearErrorMessages();
    if (priceType !== 'FIXED' && hasGroups && isEmpty(groups)) {
      dispatchResetErrorMessagesWith([{ type: errorTypes.NO_AVAILABLE_PARTNERS_GROUPS_ERROR }]);
      dispatchApiCallFinished(true);
    } else {
      this.resolveSelectedPartnerId().then(() => dispatchApiCallFinished(true));
    }
  };

  resolveSelectedPartnerId = () => {
    const {
      mainAppointmentDraft: { partner: selectedPartner },
      ChosenPartner,
      dispatchLoadPartners,
    } = this.props;

    return ChosenPartner
      ? dispatchLoadPartners().then((partners) =>
          partners.some((partner) => partner.id === selectedPartner.id) ? selectedPartner.id : '',
        )
      : Promise.resolve(selectedPartner.id);
  };

  saveSlot = () => {
    const {
      history,
      mainAppointmentDraft,
      relatedAppointmentTimeSlot,
      isMultipleAppointment,
      slotMessages,
      slotErrorMessages,

      dispatchSaveAppointmentDraft,
      dispatchResetErrorMessagesWith,
      dispatchApiCallFinished,
      dispatchClearMessages,
    } = this.props;
    if (!mainAppointmentDraft.dateSlot) {
      dispatchResetErrorMessagesWith([{ type: SLOT_DATE_NOT_SELECTED_ERROR }]);
    } else if (
      slotMessages[infoTypes.NO_AVAILABLE_TIME_SLOTS] ||
      slotErrorMessages[errorTypes.SLOT_TIME_UNAVAILABLE_ERROR]
    ) {
      dispatchClearMessages(infoTypes.NO_AVAILABLE_TIME_SLOTS);
      dispatchResetErrorMessagesWith([{ type: SLOT_TIME_UNAVAILABLE_ERROR }]);
    } else if (!mainAppointmentDraft.timeSlot.from) {
      dispatchResetErrorMessagesWith([{ type: SLOT_TIME_NOT_SELECTED_ERROR }]);
    } else if (isMultipleAppointment && !relatedAppointmentTimeSlot.from) {
      dispatchResetErrorMessagesWith([{ type: SLOT_TIME_NOT_SELECTED_ERROR }]);
    } else {
      dispatchApiCallFinished(false);
      dispatchSaveAppointmentDraft().then(() => {
        history.pushWithToken('/about-you');
      });
    }
  };

  selectShop = (shop) => {
    const {
      dispatchSelectShop,
      dispatchSelectPartner,
      dispatchClearErrorMessages,
      dispatchUpdateAppointmentType,
      dispatchApiCallFinished,
      dispatchLoadPartners,
    } = this.props;

    dispatchApiCallFinished(false);
    dispatchClearErrorMessages();
    dispatchSelectPartner();
    dispatchSelectShop(shop);
    triggerAvailabilityInteractionAnalytics('shopChange', { branchId: shop.id });
    dispatchUpdateAppointmentType().then(() => dispatchLoadPartners());
  };

  selectPartnersGroup = (group) => {
    const {
      partnersGroups,

      dispatchSelectPartnersGroup,
      dispatchSelectPartner,
      dispatchClearErrorMessages,
      dispatchUpdateAppointmentType,
      dispatchApiCallFinished,
      dispatchLoadPartners,
    } = this.props;

    dispatchApiCallFinished(false);
    dispatchClearErrorMessages();
    dispatchSelectPartner();
    dispatchSelectPartnersGroup(group.id);
    triggerPartnerGroupChangeAnalytics(getPartnerGroupName(group.name, partnersGroups));
    dispatchUpdateAppointmentType().then(() => dispatchLoadPartners());
  };

  selectPartner = (partner) => {
    const { dispatchSelectPartner } = this.props;

    if (partner !== null) {
      dispatchSelectPartner(partner);
      scrollToAnchor('chosen-partner');
    }
    triggerAvailabilityInteractionAnalytics('advisorChange');
  };

  render() {
    const {
      title,
      subtitle: { component: Subtitle },
      ShopsDropdown,
      mainAppointmentDraft,
      partners,
      prevPath,
      externalPrevPath,
      ChosenPartner,
      GroupsDropdown,
      config: { partnerTitle, remote, ...restConfig },
      isRemoteConfigEnabled,
      shopList,
      partnersGroups,
      isMultipleAppointment,
    } = this.props;

    const {
      partner: selectedPartner,
      shop: { id: selectedShopId },
      partnersGroupId: selectedPartnersGroupId,
    } = mainAppointmentDraft;

    const { isAjaxCompleted, subtitleLinkUrl } = this.state;
    const partnersGroupName = (
      partnersGroups.find((partnersGroup) => partnersGroup.id === selectedPartnersGroupId) || {}
    ).name;

    return (
      <>
        <section data-testid="SelectDateTime" data-test-ajax={isAjaxCompleted}>
          <div className={classnames({ [styles.Row]: isRemoteConfigEnabled || GroupsDropdown })}>
            {ShopsDropdown && !!shopList.length && (
              <ShopsDropdown
                selectedShopId={selectedShopId}
                shopList={shopList}
                onChange={this.selectShop}
                className={styles.Col}
              />
            )}
            {GroupsDropdown && (
              <GroupsDropdown
                partnersGroups={partnersGroups}
                selectedPartnersGroupId={selectedPartnersGroupId}
                onChange={this.selectPartnersGroup}
                className={styles.Col}
              />
            )}
            {!isMultipleAppointment && ChosenPartner && (
              <ChosenPartner
                partner={selectedPartner}
                partners={partners}
                selectPartner={this.selectPartner}
                selectedShopId={selectedShopId}
                isSeries={isRemoteConfigEnabled || !!GroupsDropdown}
                partnerTitle={partnersGroupName || partnerTitle}
                {...restConfig}
              />
            )}
          </div>
          <Calendar />
          <TimeSlots />
          <ScreenNav
            onNextClick={this.saveSlot}
            prevPath={prevPath}
            externalPrevPath={externalPrevPath}
          />
        </section>
        <ScrollToMessageTypeAnchor forTypes={[SLOT_DATE_NOT_SELECTED_ERROR]}>
          <TitleWithSubtitle
            title={title}
            className={classnames(styles.Title, {
              [styles.TitleWithSubtitle]: isRemoteConfigEnabled,
            })}
          >
            {Subtitle && (
              <Subtitle
                className={styles.Subtitle}
                classNameForSubtitleWithLink={styles.SubtitleWithLink}
                secondLineClassName={styles.Subtitle__second}
                linkUrl={subtitleLinkUrl}
              />
            )}
          </TitleWithSubtitle>
        </ScrollToMessageTypeAnchor>
      </>
    );
  }
}

export const mapStateToProps = (state) => {
  const {
    partners: { partners },
    messages: {
      app: { errorMessages: slotErrorMessages, messages: slotMessages },
    },
    envConfig,
  } = state;
  const mainAppointmentDraft = appointmentDraftSelectors.selectAppointmentDraft(state);

  const {
    appointmentType: { id: mainAppointmentTypeId },
  } = mainAppointmentDraft;

  const relatedAppointmentDraft = appointmentDraftSelectors.selectRelatedAppointmentDraftById()(
    state,
  ) || { appointmentType: {} };

  const selectedSlotErrorMessages = calendarSelectors.selectSlotMessages(slotErrorMessages);
  const selectedSlotMessages = calendarSelectors.selectSlotMessages(slotMessages);
  const remoteConfig = envConfigSelectors.remoteConfig(mainAppointmentTypeId)(state);
  const externalPrevPath =
    envConfigSelectors.selectExternalPrevPathForSelectDate(mainAppointmentTypeId)(state);
  const { inStoreAppointmentTypeId, virtualAppointmentTypeId } =
    envConfigSelectors.selectAppointmentTypeConfig(envConfig, mainAppointmentTypeId);
  const { virtualStoreId } = envConfigSelectors.selectAppointmentTypeConfig(
    envConfig,
    virtualAppointmentTypeId,
  );
  const chosenPartner = envConfigSelectors.selectChosenPartner(mainAppointmentTypeId)(state);
  const groupsDropdown = envConfigSelectors.selectGroupsDropdown(state);
  const prevPath = envConfigSelectors.selectDatePrevPath(mainAppointmentTypeId)(state);

  const subtitleComponent =
    envConfigSelectors.selectSubtitleComponent(mainAppointmentTypeId)(state);

  return {
    mainAppointmentDraft,
    relatedAppointmentTimeSlot: relatedAppointmentDraft.timeSlot,
    partners,
    config: envConfigSelectors.selectConfig(mainAppointmentTypeId)(state),
    ChosenPartner: chosenPartner,
    GroupsDropdown: groupsDropdown,
    externalPrevPath,
    prevPath: externalPrevPath ? null : prevPath,
    slotErrorMessages: selectedSlotErrorMessages,
    slotMessages: selectedSlotMessages,
    isRemoteConfigEnabled: isNotEmpty(remoteConfig),
    title: remoteConfig.selectDateTitle || 'Choose a date for your appointment',
    ShopsDropdown: remoteConfig.ShopsDropdown,
    shopList: shopsSelectors.selectShopList(state),
    searchQuery: journeyStateSelectors.selectSearchQuery(state),
    subtitle: subtitleComponent && {
      component: subtitleComponent,
      inStoreAppointmentTypeId,
      virtualAppointmentTypeId,
      virtualStoreId,
    },
    partnersGroups: appointmentDraftSelectors.selectPartnersGroups(state),
    isMultipleAppointment: appointmentDraftSelectors.isMultipleAppointment(state),
  };
};

const mapDispatchToProps = {
  dispatchSaveAppointmentDraft: appointmentDraftActions.saveAppointmentDraft,
  dispatchSelectPartner: appointmentDraftActions.setPartner,
  dispatchSelectShop: appointmentDraftActions.setShop,
  dispatchSelectPartnersGroup: appointmentDraftActions.setPartnersGroupId,
  dispatchUpdateAppointmentType: appointmentDraftActions.updateAppointmentType,

  dispatchResetErrorMessagesWith: errorMessagesActions.resetErrorMessagesWith,
  dispatchRemoveSingleErrorMessage: errorMessagesActions.removeSingleErrorMessage,
  dispatchClearErrorMessages: errorMessagesActions.clearErrorMessages,
  dispatchApiCallFinished: apiCallFinished,
  dispatchLoadPartners: loadPartners,
  dispatchLoadShops: shopsActions.loadShops,
  dispatchClearMessages: messagesActions.clearMessages,
};

export const ConnectedSelectDateTime = withRestorableDraft(
  withRequiredPrecondition(
    connect(mapStateToProps, mapDispatchToProps)(withProgressBar(SelectDateTime)),
  ),
);

const groupsModel = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  duration: PropTypes.number.isRequired,
};

const appointmentTypeModel = {
  id: PropTypes.string.isRequired,
  duration: PropTypes.number.isRequired,
  minAdvance: PropTypes.number.isRequired,
  maxAdvance: PropTypes.number.isRequired,
  localId: PropTypes.string.isRequired,
  groups: PropTypes.arrayOf(PropTypes.shape(groupsModel)),
  hasGroups: PropTypes.bool.isRequired,
};

const shopModel = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

const partnerModel = {};

const timeSlotModel = {
  eventId: PropTypes.string.isRequired,
  from: PropTypes.number.isRequired,
  to: PropTypes.number,
};

SelectDateTime.propTypes = {
  history: PropTypes.shape({}).isRequired,
  isMultipleAppointment: PropTypes.bool.isRequired,

  relatedAppointmentTimeSlot: PropTypes.shape(timeSlotModel),

  mainAppointmentDraft: PropTypes.shape({
    appointmentType: PropTypes.shape(appointmentTypeModel).isRequired,
    shop: PropTypes.shape(shopModel).isRequired,
    partner: PropTypes.shape(partnerModel).isRequired,
    dateSlot: PropTypes.string.isRequired,
    timeSlot: PropTypes.shape(timeSlotModel).isRequired,
  }).isRequired,
  partners: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  config: PropTypes.shape({
    partnerTitle: PropTypes.string,
    Calendar: PropTypes.shape({
      calendarTimeSlotsIntro: PropTypes.func,
      timeSlotsList: PropTypes.func,
      timeSlotSectionTitle: PropTypes.string,
      formatTimeSlots: PropTypes.func,
    }),
    remote: PropTypes.shape({
      appointment: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }),
    }),
  }).isRequired,
  ChosenPartner: PropTypes.func,
  GroupsDropdown: PropTypes.func,
  prevPath: PropTypes.string,
  externalPrevPath: PropTypes.string,
  slotErrorMessages: PropTypes.shape({
    type: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  slotMessages: PropTypes.shape({
    type: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  isRemoteConfigEnabled: PropTypes.bool,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.shape({
    component: PropTypes.func,
    inStoreAppointmentTypeId: PropTypes.string,
    virtualAppointmentTypeId: PropTypes.string,
    virtualStoreId: PropTypes.string,
  }),
  ShopsDropdown: PropTypes.func,
  shopList: PropTypes.arrayOf(PropTypes.shape(shopModel)),
  searchQuery: PropTypes.string.isRequired,
  partnersGroups: PropTypes.arrayOf(PropTypes.shape(groupsModel)),

  dispatchSelectPartner: PropTypes.func.isRequired,
  dispatchSelectPartnersGroup: PropTypes.func.isRequired,
  dispatchResetErrorMessagesWith: PropTypes.func.isRequired,
  dispatchRemoveSingleErrorMessage: PropTypes.func.isRequired,
  dispatchSaveAppointmentDraft: PropTypes.func.isRequired,
  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchLoadPartners: PropTypes.func.isRequired,
  dispatchClearErrorMessages: PropTypes.func.isRequired,
  dispatchLoadShops: PropTypes.func.isRequired,
  dispatchSelectShop: PropTypes.func.isRequired,
  dispatchUpdateAppointmentType: PropTypes.func.isRequired,
  dispatchClearMessages: PropTypes.func.isRequired,
};

SelectDateTime.defaultProps = {
  isRemoteConfigEnabled: false,
  subtitle: {},
  ShopsDropdown: null,
  shopList: [],
  prevPath: null,
  externalPrevPath: null,
  ChosenPartner: undefined,
  GroupsDropdown: undefined,
  partnersGroups: [],
  relatedAppointmentTimeSlot: null,
};
